import { Styled } from "Areas/Sidebar/Common";
import getHighlighted from "Common/componentUtils/highlight";
import noSelectedText from "Common/componentUtils/noSelectedText";
import { formatDateTime } from "Common/dateTimeUtils/format";
import { TASK_STATUS, taskStatusColorMap, taskStatusIconMap, taskStatusLocale } from "Common/dictionaries/taskStatus";
import { security } from "Common/permissions";
import Menu from "Components/menu";
import RotatedArrow from "Components/rotatedArrow";
import SlideUpDown from "Components/slideUpDown";
import { IWithTooltip } from "Components/withPopover";
import classNames from "classnames";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import React, { memo, useCallback, useMemo } from "react";
import locale from "./task.locale";
import styles from "./task.module.css";
import TaskShowButton from "Areas/Sidebar/Documents/tabTasks/tasks.inner/task/taskShowButton/taskShowButton";
const userId = security.getCurrentUserId();
function Task(props) {
    var _a, _b;
    const handleOpenedToggle = useCallback(() => {
        noSelectedText() && props.onOpenedToggle && props.onOpenedToggle(props.data.Id);
    }, [props.data.Id, props.onOpenedToggle]);
    const statusPopoverContent = useMemo(() => (React.createElement("div", { className: taskStatusColorMap[props.data.Status] }, taskStatusLocale[props.data.Status])), [props.data.Status]);
    const canBeStopped = useMemo(() => includes([TASK_STATUS.PENDING, TASK_STATUS.IN_PROGRESS], props.data.Status), [props.data.Status]);
    const canHighlightResults = useMemo(() => TASK_STATUS.COMPLETED === props.data.Status, [props.data.Status]);
    const menuItems = useMemo(() => [
        {
            icon: classNames("icon-check font-h5", !canHighlightResults ? "text-gray-500" : "text-gray-400"),
            textClassName: !canHighlightResults ? "text-gray-500" : undefined,
            text: locale.HIGHLIGHT_RESULTS,
            onClick: () => {
                props.onHighlightTaskResults &&
                    props.data.Document &&
                    props.onHighlightTaskResults(map(props.data.SubTasks, "Id"), props.data.Document.Id);
            },
            disabled: !canHighlightResults
        },
        {
            icon: classNames("icon-stopwatch font-h5", !canBeStopped ? "text-gray-500" : "text-gray-400"),
            textClassName: !canBeStopped ? "text-gray-500" : undefined,
            text: locale.STOP_TASK,
            onClick: () => {
                props.onStopRequestTask && props.onStopRequestTask(props.data.Id);
            },
            disabled: !canBeStopped
        },
        {
            icon: "icon-refresh font-h5 text-gray-400",
            text: locale.RELOAD_TASK,
            onClick: () => {
                props.onRestartRequestTask && props.onRestartRequestTask(props.data.Id);
            }
        },
        {
            icon: "icon-inputlog font-h5 text-gray-400",
            text: locale.WATCH_TASK_LOGS,
            onClick: () => {
                props.onTaskLogOpen &&
                    props.onTaskLogOpen(true, props.data.SubTasks.map(s => s.Id), props.data.Name);
            }
        }
    ], [
        props.data,
        canBeStopped,
        canHighlightResults,
        props.onTaskLogOpen,
        props.onStopRequestTask,
        props.onRestartRequestTask,
        props.onHighlightTaskResults
    ]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: classNames("p-0_5 clickable", props.className, props.opened ? styles["task-active"] : "border-rounded-medium"), onClick: handleOpenedToggle },
            React.createElement("div", { className: "grid-cols-1fr-auto v-start-items" },
                React.createElement("div", { className: "grid-cols-auto-1fr v-start-items" },
                    React.createElement("div", { className: "d-flex" },
                        React.createElement(Styled.MenuLinkActionsPanel, { actionIconColor: props.actionIconColor },
                            React.createElement(RotatedArrow, { className: "flex-box-centered-content text-light-on-surface-variant", open: props.opened, size: 10 })),
                        React.createElement("div", { className: "pr-0_5" },
                            React.createElement(IWithTooltip, { className: classNames(taskStatusIconMap[props.data.Status], taskStatusColorMap[props.data.Status], "font-h5", "d-block"), popoverContent: statusPopoverContent }))),
                    React.createElement("div", { className: "text-overflow-ellipsis nowrap text-default", title: props.data.Name },
                        React.createElement("span", { className: "fw-6" }, getHighlighted(props.data.Name, props.filterText)),
                        React.createElement("div", { className: "text-default font-small" }, formatDateTime(props.data.Date)))),
                !isEmpty(menuItems) && (React.createElement(Styled.MenuLinkActionsPanel, { actionIconColor: props.actionIconColor },
                    React.createElement(Menu, { popoverClassName: "dark-popover1", togglerClassName: "no-border no-default-colors", togglerIconClassName: "icon-dots text-default", togglerHeight: 26, togglerWidth: 26, items: menuItems }))))),
        React.createElement(SlideUpDown, { className: props.detailsClassName, open: Boolean(props.opened) },
            React.createElement("div", { className: `bg-new-gray ${styles["slide-up-down"]}` },
                React.createElement("div", null,
                    React.createElement("div", { className: "text-default font-small" }, locale.SOURCE_TASKS),
                    map(props.data.SubTasks, item => (React.createElement("div", { key: item.Id, className: "grid-cols-auto-1fr v-center-items pt-0_25 pb-0_25" },
                        React.createElement("div", { className: "flex-box-centered-content5 pr-0_75 line-height-1" },
                            React.createElement(IWithTooltip, { className: classNames(taskStatusIconMap[item.Status], taskStatusColorMap[item.Status], "font-h5"), popoverContent: React.createElement("div", { className: taskStatusColorMap[item.Status] }, taskStatusLocale[item.Status]) })),
                        React.createElement("div", { className: "caption-with-actions-panel" },
                            React.createElement("div", { className: "text-overflow-ellipsis nowrap text-default", title: item.Name }, getHighlighted(item.Name, props.filterText)),
                            (item.Status === TASK_STATUS.COMPLETED || item.Status === TASK_STATUS.ERROR) && (React.createElement("div", { className: "grid-cols-auto no-gap" },
                                React.createElement("div", { className: styles.total }, item.Total),
                                Boolean(props.onToggleShowHideResults &&
                                    props.showedResultsSourceTaskIds &&
                                    item.Total) && React.createElement(TaskShowButton, { item: item, ...props })))))))),
                React.createElement("div", { className: styles.author },
                    React.createElement("div", { className: "font-small mr-0_5 text-default" },
                        locale.USER,
                        ":"),
                    React.createElement("div", { className: classNames("grid-cols-auto-1fr no-gap v-start-items overflow-hidden font-small", !props.data.User ? "mb-0_75" : "mb-0_5") },
                        React.createElement("div", { className: "text-default text-overflow-ellipsis" }, `${((_a = props.data.User) === null || _a === void 0 ? void 0 : _a.Name) || locale.UNKNOWN}${((_b = props.data.User) === null || _b === void 0 ? void 0 : _b.Id) === userId ? ` (${locale.YOU})` : ""}`)))))));
}
export default memo(Task);
